import { FC, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { LinkButton, Modal } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { useUserContext } from 'src/context/user';
import { TrackingApi } from 'src/api';
import { MistakeUpsellTrackingAction } from 'src/api/tracking/types';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { markMistakeUpsellShown } from './mistakeUpsellStorage';
import * as S from './styles';

const msgs = defineMessages({
  title: {
    id: 'mistake_upsell_title',
    defaultMessage:
      '{n, plural, one {This word tripped you up} other {These # words tripped you up}}',
  },
  body: {
    id: 'mistake_upsell_body',
    defaultMessage: "Pro's Difficult Words drills them until they stick.",
  },
  cta: {
    id: 'mistake_upsell_cta',
    defaultMessage: 'Drill these with Pro',
  },
  skip: {
    id: 'mistake_upsell_skip',
    defaultMessage: 'Review them the slow way',
  },
  moreWords: {
    id: 'mistake_upsell_more_words',
    defaultMessage: '{count, plural, other {+# more}}',
  },
});

const MAX_VISIBLE_WORDS = 3;
const MISTAKE_UPSELL_SOURCE = 'mistake_recovery';

export interface MistakeUpsellWord {
  targetValue: string;
  sourceValue: string;
}

export interface MistakeUpsellProps {
  missedWords: MistakeUpsellWord[];
  onContinue: () => void;
  /** Extra query params carried into the plans page the CTA links to. */
  plansQuery?: Record<string, string>;
}

export const MistakeUpsell: FC<MistakeUpsellProps> = ({ missedWords, onContinue, plansQuery }) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const languagePairId = getLanguagePairId();
  const visibleWords = missedWords.slice(0, MAX_VISIBLE_WORDS);
  const extraWordsCount = missedWords.length - visibleWords.length;
  const title = intl.formatMessage(msgs.title, { n: missedWords.length });

  const trackAction = useCallback(
    (action: MistakeUpsellTrackingAction) => {
      void TrackingApi.trackUserAction(action, 'mistake_upsell', languagePairId, !!user?.isPro, {
        source: MISTAKE_UPSELL_SOURCE,
      });
    },
    [languagePairId, user?.isPro],
  );

  useEffect(() => {
    markMistakeUpsellShown(user);
    trackAction('screen_viewed');
    // Fire once on mount for this surface
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribeClick = useCallback(() => {
    trackAction('clicked_subscribe');
  }, [trackAction]);

  const handleDismiss = useCallback(() => {
    trackAction('paywall_dismissed');
    onContinue();
  }, [trackAction, onContinue]);

  return (
    <Modal isOpen fullSize hideCloseButton handleClose={handleDismiss}>
      <S.ContentOuterContainer data-testid="mistake-upsell">
        <S.ContentInnerContainer>
          <S.IconBadge aria-hidden>
            <S.BadgeIcon name="difficultWordsFilled" />
          </S.IconBadge>
          <S.Title>{title}</S.Title>
          <S.WordList aria-label={title}>
            {visibleWords.map(word => (
              <S.WordRow key={word.targetValue}>
                <S.WordTarget>{word.targetValue}</S.WordTarget>
                <S.WordSource>{word.sourceValue}</S.WordSource>
              </S.WordRow>
            ))}
          </S.WordList>
          {extraWordsCount > 0 && (
            <S.MoreWords>
              {intl.formatMessage(msgs.moreWords, { count: extraWordsCount })}
            </S.MoreWords>
          )}
          <S.Description>{intl.formatMessage(msgs.body)}</S.Description>
          <S.ButtonContainer>
            <LinkButton
              buttonType="primary"
              buttonSize="large"
              wide
              href={{
                pathname: ROUTES.payment.plans,
                query: { ...plansQuery, source: MISTAKE_UPSELL_SOURCE },
              }}
              onClick={handleSubscribeClick}
            >
              {intl.formatMessage(msgs.cta)}
            </LinkButton>
            <S.FreeLink type="button" onClick={handleDismiss} data-testid="mistake-upsell-skip">
              {intl.formatMessage(msgs.skip)}
            </S.FreeLink>
          </S.ButtonContainer>
        </S.ContentInnerContainer>
      </S.ContentOuterContainer>
    </Modal>
  );
};
